import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ModalHeader = styled.div`
  background-color: #00bbd3;
  color: white;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:hover {
    opacity: 0.8;
  }
`;

const PDFContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #525252;
  position: relative;
`;

const PDFIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;

  /* Android-specific fixes */
  -webkit-overflow-scrolling: touch;
  overflow: auto;
`;

const LoadingMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 16px;
  text-align: center;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #ff6b6b;
  font-size: 16px;
  text-align: center;
  padding: 20px;
  max-width: 80%;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PDFViewerModal = ({ isOpen, onClose, pdfUrl, title = "ผลการตรวจสุขภาพ" }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
      // ป้องกันการ scroll ของ body เมื่อเปิด modal
      document.body.style.overflow = 'hidden';
    } else {
      // คืนค่า scroll ของ body เมื่อปิด modal
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleIframeLoad = () => {
    setLoading(false);
    console.log('✅ PDF iframe loaded successfully');
  };

  const handleIframeError = () => {
    setLoading(false);
    setError('ไม่สามารถโหลด PDF ได้ กรุณาลองใหม่อีกครั้ง');
    console.error('❌ PDF iframe failed to load');
  };

  const handleClose = () => {
    setLoading(true);
    setError(null);
    onClose();
  };

  // ตรวจสอบว่าเป็น Android หรือไม่
  const isAndroid = /Android/i.test(navigator.userAgent);

  // สร้าง URL สำหรับ iframe โดยเพิ่ม parameters สำหรับ mobile
  const getOptimizedPDFUrl = () => {
    if (!pdfUrl) return '';

    // ถ้า URL เป็น PDF file โดยตรง ให้เพิ่ม #view=FitH เพื่อ fit กับความกว้างหน้าจอ
    if (pdfUrl.toLowerCase().endsWith('.pdf')) {
      return `${pdfUrl}#view=FitH&toolbar=0&navpanes=0&scrollbar=1`;
    }

    // ถ้าเป็น URL ของ public healthbook
    // สำหรับ Android ให้เพิ่ม query parameter เพื่อบอกว่าเปิดจาก mobile
    if (isAndroid) {
      const separator = pdfUrl.includes('?') ? '&' : '?';
      return `${pdfUrl}${separator}mobile=android&viewport=fit`;
    }

    return pdfUrl;
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen}>
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
        <CloseButton onClick={handleClose} aria-label="ปิด">
          ×
        </CloseButton>
      </ModalHeader>

      <PDFContainer>
        {loading && <LoadingMessage>กำลังโหลด PDF...</LoadingMessage>}
        {error && <ErrorMessage>{error}</ErrorMessage>}

        {pdfUrl && (
          <PDFIframe
            src={getOptimizedPDFUrl()}
            title="PDF Viewer"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            allow="fullscreen"
            loading="eager"
          />
        )}
      </PDFContainer>
    </ModalOverlay>
  );
};

export default PDFViewerModal;
